import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Clock, Heart, Users, FileText, Navigation, Search, BookOpen, AlertCircle, TrendingUp } from 'lucide-react';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-section relative min-h-screen flex items-center justify-center">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1533134486753-c833f0ed4866?w=1920&q=80)',
            opacity: 0.15
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-navy via-navy/95 to-navy" />
        
        <div className="container mx-auto px-6 relative z-10 text-center">
          <h1 className="hero-title text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Find Ontario<br />Missing Boys
          </h1>
          <p className="hero-subtitle text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            A province-wide, community-led advocacy initiative actively supporting Black youth and families 
            impacted by disappearance, exploitation, trafficking risk, and system involvement across Ontario.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://form.jotform.com/260245121503038"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center justify-center gap-2"
            >
              Share Your Experience (Anonymous)
              <ArrowRight size={20} />
            </a>
            <a
              href="#about-initiative"
              className="btn-secondary inline-flex items-center justify-center gap-2"
            >
              Learn More
            </a>
          </div>
        </div>
      </section>

      {/* Credibility Anchor Section */}
      <section id="about-initiative" className="py-16 bg-charcoal border-b border-gold-dim">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <p className="text-lg md:text-xl text-gray-300 leading-relaxed text-left">
              <strong className="text-white">Find Ontario Missing Boys (FOMB)</strong> is a province-wide, community-led advocacy initiative founded by producer and systems advocate <strong className="text-white">Shana McCalla, Founder & Lead Advocate</strong>, whose investigative and advocacy work has helped elevate awareness and dialogue around Black youth disappearances, exploitation, and systemic response gaps across Ontario. Grounded in direct frontline engagement with families, caregivers, educators, and community agencies, FOMB operates at the intersection of prevention, advocacy, and systems navigation — supporting families during crisis while working to improve institutional responses across justice, child welfare, education, and community safety sectors. Through lived-experience leadership, trusted grassroots relationships, and active collaboration with partners across the province, FOMB is building coordinated pathways toward safer outcomes, early intervention, and long-term systemic change for vulnerable youth. FOMB currently supports families across multiple Ontario regions identified as high-risk corridors while working alongside educators, community agencies, and advocacy partners to strengthen coordinated response mechanisms province-wide. As FOMB continues to expand its reach, the initiative is positioned to scale coordinated prevention and response pathways across Ontario through strategic partnerships and collaborative funding opportunities that strengthen community-based support systems.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 bg-navy">
        <div className="container mx-auto px-6">
          <h2 className="section-title text-4xl font-bold text-white mb-12 text-center">
            What We Do
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="info-card">
              <div className="icon-wrapper mb-6">
                <Heart size={32} className="text-gold" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Family & Caregiver Support
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Providing direct support, guidance, and crisis navigation for families and caregivers 
                experiencing youth disappearance, exploitation, or system involvement.
              </p>
            </div>

            <div className="info-card">
              <div className="icon-wrapper mb-6">
                <Navigation size={32} className="text-gold" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Systems Navigation & Advocacy
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Helping families understand and navigate complex institutional systems including police, 
                child welfare, education, and community safety agencies.
              </p>
            </div>

            <div className="info-card">
              <div className="icon-wrapper mb-6">
                <BookOpen size={32} className="text-gold" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Community Awareness & Education
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Building community knowledge about youth exploitation prevention, early warning signs, 
                and protective factors through trauma-informed education.
              </p>
            </div>

            <div className="info-card">
              <div className="icon-wrapper mb-6">
                <Shield size={32} className="text-gold" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Prevention & Early Intervention
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Working proactively with youth, families, and community partners to identify risk factors 
                and build prevention pathways that reduce vulnerability before crises occur.
              </p>
            </div>

            <div className="info-card">
              <div className="icon-wrapper mb-6">
                <Search size={32} className="text-gold" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Research, Story Collection & Policy Advocacy
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Documenting lived experiences, identifying systemic gaps, and advocating for policy changes 
                that improve outcomes for vulnerable youth across Ontario.
              </p>
            </div>

            <div className="info-card">
              <div className="icon-wrapper mb-6">
                <Users size={32} className="text-gold" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Cross-Sector Collaboration
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Building partnerships with community agencies, justice sectors, child welfare, education, 
                and public health to create coordinated support pathways.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Work Matters */}
      <section className="py-20 bg-charcoal">
        <div className="container mx-auto px-6">
          <h2 className="section-title text-4xl font-bold text-white mb-12 text-center">
            Why This Work Matters
          </h2>
          
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="bg-navy p-8 rounded-lg border border-gold-dim">
              <div className="flex items-start gap-4">
                <AlertCircle size={32} className="text-gold flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">Disproportionate Impact</h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Black youth across Ontario face disproportionate risk of disappearance, exploitation, 
                    criminal coercion, and trafficking. Many cases involve complex vulnerabilities including 
                    system involvement, housing instability, mental health challenges, and exposure to violence.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-navy p-8 rounded-lg border border-gold-dim">
              <div className="flex items-start gap-4">
                <TrendingUp size={32} className="text-gold flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">Systemic Response Gaps</h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Families often encounter fragmented institutional responses, delayed action, inadequate 
                    follow-up, and limited access to coordinated support. Many cases fall through gaps between 
                    justice, child welfare, education, and community safety sectors. Systemic barriers prevent 
                    timely intervention and leave families navigating complex systems without clear guidance or support.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-navy p-8 rounded-lg border border-gold-dim">
              <div className="flex items-start gap-4">
                <Heart size={32} className="text-gold flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-bold text-white mb-4">Community-Led Solutions</h3>
                  <p className="text-gray-300 leading-relaxed mb-4">
                    FOMB exists to bridge these response gaps through trusted community relationships, 
                    lived-experience leadership, and direct family support. By centering the voices of 
                    those most affected, we work toward earlier intervention, safer outcomes, and long-term 
                    systemic change that protects vulnerable youth.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters - Original 3 Cards */}
      <section id="why-matters" className="py-20 bg-navy border-t border-gold-dim">
        <div className="container mx-auto px-6">
          <h2 className="section-title text-4xl font-bold text-white mb-12 text-center">
            What Families Deserve
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="info-card">
              <div className="icon-wrapper mb-6">
                <Clock size={32} className="text-gold" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Immediate Response
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Every report of a missing or endangered youth should be taken seriously immediately, 
                without delay, dismissal, or assumptions based on background.
              </p>
            </div>

            <div className="info-card">
              <div className="icon-wrapper mb-6">
                <Heart size={32} className="text-gold" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Coordinated Support
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Families deserve timely follow-up, clear communication, compassionate support, 
                and coordinated responses across all involved agencies.
              </p>
            </div>

            <div className="info-card">
              <div className="icon-wrapper mb-6">
                <Shield size={32} className="text-gold" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">
                Accountability
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Community data and lived experiences help strengthen accountability, improve 
                institutional responses, and advance safety for all youth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We're Learning Section */}
      <section className="py-20 bg-charcoal">
        <div className="container mx-auto px-6">
          <h2 className="section-title text-4xl font-bold text-white mb-6 text-center">
            What We're Learning from Community
          </h2>
          <p className="text-gray-300 text-center max-w-3xl mx-auto mb-12 leading-relaxed">
            Through anonymous story collection and lived-experience documentation, we're identifying 
            patterns in institutional responses and building evidence for systemic change.
          </p>
          
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="collection-item">
              <div className="flex items-start gap-4">
                <div className="collection-number">1</div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">First Response Time & Quality</h3>
                  <p className="text-gray-400">
                    How quickly were initial reports acknowledged? Was the response immediate, 
                    or were there delays, dismissals, or assumptions based on youth background?
                  </p>
                </div>
              </div>
            </div>

            <div className="collection-item">
              <div className="flex items-start gap-4">
                <div className="collection-number">2</div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Investigation & Follow-up</h3>
                  <p className="text-gray-400">
                    Was an investigator assigned? How was communication handled with families? 
                    Were there gaps in coordination across agencies?
                  </p>
                </div>
              </div>
            </div>

            <div className="collection-item">
              <div className="flex items-start gap-4">
                <div className="collection-number">3</div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Support & Navigation Provided</h3>
                  <p className="text-gray-400">
                    What support, resources, or guidance was (or was not) provided to families 
                    during crisis? Were families left to navigate systems alone?
                  </p>
                </div>
              </div>
            </div>

            <div className="collection-item">
              <div className="flex items-start gap-4">
                <div className="collection-number">4</div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Understanding Vulnerability & Risk</h3>
                  <p className="text-gray-400">
                    Were underlying vulnerabilities (exploitation, trafficking risk, coercion) 
                    recognized and addressed? Or were youth treated as "runaways" without deeper assessment?
                  </p>
                </div>
              </div>
            </div>

            <div className="collection-item">
              <div className="flex items-start gap-4">
                <div className="collection-number">5</div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">What Should Be Different</h3>
                  <p className="text-gray-400">
                    What do families wish was done differently? What changes would improve 
                    outcomes, safety, and institutional accountability for vulnerable youth?
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <a
              href="https://form.jotform.com/260245121503038"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center gap-2"
            >
              Share Your Story Anonymously
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* Safety Disclaimer */}
      <section className="py-16 bg-navy border-t border-gold-dim">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gold/10 rounded-full mb-6">
              <Shield size={32} className="text-gold" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-4">
              Important Notice
            </h3>
            <p className="text-gray-400 leading-relaxed">
              FOMB is an advocacy and support initiative and does not replace emergency services. 
              If someone is in immediate danger, <strong className="text-gold">call 911</strong>.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
